import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AuthProvider } from '@/auth/AuthProvider'
import { RequireAuth, RequirePortalAccess, RequireRole } from '@/auth/guards'
import { AppShell } from '@/components/AppShell'
import { NotBuiltYet } from '@/components/NotBuiltYet'
import { FullPageMessage } from '@/components/FullPageMessage'
import { FEATURES } from '@/lib/features'

import { SignIn } from '@/routes/SignIn'
import { AuthCallback } from '@/routes/AuthCallback'
import { AuthConfirm } from '@/routes/AuthConfirm'
import { ForgotPassword } from '@/routes/ForgotPassword'
import { SetPassword } from '@/routes/SetPassword'
import { Waiting } from '@/routes/Waiting'
import { Dashboard } from '@/routes/Dashboard'
import { ReportsPage } from '@/routes/ReportsPage'
import { TeamPage } from '@/routes/TeamPage'
import { LibraryPage } from '@/features/library/LibraryPage'
import { EvaluationPage } from '@/features/evaluation/EvaluationPage'
import { AdminHome } from '@/features/admin/AdminHome'
import { OrganisationsPage } from '@/features/admin/OrganisationsPage'
import { OrganisationDetailPage } from '@/features/admin/OrganisationDetailPage'
import { ResourcesPage } from '@/features/admin/ResourcesPage'
import { AggregateReporting } from '@/features/admin/AggregateReporting'
import { KtmStaffPage } from '@/features/admin/KtmStaffPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Open routes */}
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/confirm" element={<AuthConfirm />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />

            {/* Signed in, but possibly not yet linked to anything */}
            <Route element={<RequireAuth />}>
              <Route path="/waiting" element={<Waiting />} />

              <Route element={<AppShell />}>
                <Route index element={<Dashboard />} />

                {/* Gated on an active subscription (R7) */}
                <Route element={<RequirePortalAccess />}>
                  <Route path="/library" element={<LibraryPage />} />
                  {/* Not ready for partners yet: see src/lib/features.ts */}
                  {FEATURES.evaluation && <Route path="/evaluation" element={<EvaluationPage />} />}
                  {FEATURES.reports && <Route path="/reports" element={<ReportsPage />} />}
                  {FEATURES.orders && <Route
                    path="/orders"
                    element={
                      <NotBuiltYet
                        title="Orders"
                        body="Repeat purchasing, order history and delivery tracking sit here. The tables and policies exist; the screens wait on the payment gateway decision, because the card and invoice routes shape the checkout differently."
                      />
                    }
                  />}
                </Route>

                <Route element={<RequireRole roles={['org_admin']} />}>
                  <Route path="/team" element={<TeamPage />} />
                </Route>

                {/* Ki Tua o Matariki only */}
                <Route element={<RequireRole roles={['ktm_admin', 'ktm_staff']} />}>
                  <Route path="/admin" element={<AdminHome />}>
                    <Route path="organisations" element={<OrganisationsPage />} />
                    <Route path="organisations/:orgId" element={<OrganisationDetailPage />} />
                    <Route path="reporting" element={<AggregateReporting />} />
                    <Route path="resources" element={<ResourcesPage />} />
                    {/* Managing the allowlist is a ktm_admin action only. */}
                    <Route element={<RequireRole roles={['ktm_admin']} />}>
                      <Route path="staff" element={<KtmStaffPage />} />
                    </Route>
                    <Route
                      path="stock"
                      element={
                        <NotBuiltYet
                          title="Stock and fulfilment"
                          body="Stock levels, reorder points and the picking queue. Depends on the print supplier and print-on-demand-versus-bulk decisions, since those set what a reorder point actually means."
                        />
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <NotBuiltYet
                          title="Access and audit log"
                          body="Who opened, changed or exported what. Every row is already being written by database triggers and the resource function, so this screen is a reader over data that exists."
                        />
                      }
                    />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route
              path="*"
              element={
                <FullPageMessage
                  title="Page not found"
                  body="That address does not exist in the portal. Check the link, or start again from the home page."
                />
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
